import { useState, useRef, useEffect, useMemo, useCallback } from "react"
import { Badge } from "@/components/ui/badge"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { X, GripVertical, HelpCircle } from "lucide-react"

export interface ConditionalOption {
  label: string
  value: string
}

interface Option {
  label: string
  value: string
}

interface ParsedQuery {
  metrics: string[]
  dimensions: string[]
  pivot: string[]
  dateRange: { from?: string | Date; to?: string | Date } | null
  conditionals: string
}

interface SmartReportInputProps {
  metricOptions: Option[]
  dimensionOptions: Option[]
  onQueryParsed: (parsed: ParsedQuery) => void
  placeholder?: string
  // Initial values from parent (for syncing with Advanced Mode)
  initialMetrics?: string[]
  initialDimensions?: string[]
  initialPivot?: string[]
  initialDateRange?: { from?: string | Date; to?: string | Date } | null
  initialConditionals?: string
  // Callback to fetch dimension values for autocomplete
  onFetchDimensionValues?: (dimension: string) => Promise<string[]>
}

// Date parsing helpers
function getQuarterDates(year: number, quarter: number): { start: string; end: string } {
  const quarters: Record<number, { start: string; end: string }> = {
    1: { start: `${year}-01-01`, end: `${year}-03-31` },
    2: { start: `${year}-04-01`, end: `${year}-06-30` },
    3: { start: `${year}-07-01`, end: `${year}-09-30` },
    4: { start: `${year}-10-01`, end: `${year}-12-31` },
  }
  return quarters[quarter]
}

function getCurrentQuarter(): number {
  const month = new Date().getMonth() + 1
  return Math.ceil(month / 3)
}

function getWeekDates(date: Date): { start: Date; end: Date } {
  const day = date.getDay()
  const diff = date.getDate() - day + (day === 0 ? -6 : 1) // Adjust for Monday start
  const monday = new Date(date)
  monday.setDate(diff)
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  return { start: monday, end: sunday }
}

function formatDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getLastDayOfMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

function parseDatePhrase(text: string): { from?: string | Date; to?: string | Date } | null {
  const lowerText = text.toLowerCase().trim()
  const today = new Date()
  const currentYear = today.getFullYear()
  const currentMonth = today.getMonth()
  const currentQuarter = getCurrentQuarter()

  // "last X days/weeks/months" - rolling window
  const rollingMatch = lowerText.match(/last\s+(\d+)\s+(days?|weeks?|months?)/)
  if (rollingMatch) {
    const num = parseInt(rollingMatch[1])
    const unit = rollingMatch[2]
    let days = num
    if (unit.startsWith('week')) days = num * 7
    if (unit.startsWith('month')) days = num * 30
    return { from: `d-${days}`, to: 'd0' }
  }

  // "yesterday" or "last day"
  if (lowerText.includes('yesterday') || /last\s+day\b/.test(lowerText)) {
    return { from: 'd-1', to: 'd-1' }
  }

  // "today" or "this day"
  if (lowerText.includes('today') || /this\s+day\b/.test(lowerText)) {
    return { from: 'd0', to: 'd0' }
  }

  // "last week" - previous calendar week
  if (/last\s+week\b/.test(lowerText) && !/last\s+\d+\s+weeks?/.test(lowerText)) {
    const lastWeek = new Date(today)
    lastWeek.setDate(today.getDate() - 7)
    const { start, end } = getWeekDates(lastWeek)
    return { from: formatDate(start), to: formatDate(end) }
  }

  // "this week" - current calendar week
  if (/this\s+week\b/.test(lowerText)) {
    const { start, end } = getWeekDates(today)
    return { from: formatDate(start), to: formatDate(end) }
  }

  // "last month" - previous calendar month
  if (/last\s+month\b/.test(lowerText) && !/last\s+\d+\s+months?/.test(lowerText)) {
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1
    const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear
    const lastDay = getLastDayOfMonth(lastMonthYear, lastMonth)
    return {
      from: `${lastMonthYear}-${String(lastMonth + 1).padStart(2, '0')}-01`,
      to: `${lastMonthYear}-${String(lastMonth + 1).padStart(2, '0')}-${lastDay}`
    }
  }

  // "this month" - current calendar month
  if (/this\s+month\b/.test(lowerText)) {
    const lastDay = getLastDayOfMonth(currentYear, currentMonth)
    return {
      from: `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-01`,
      to: `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${lastDay}`
    }
  }

  // "last quarter" - previous calendar quarter
  if (/last\s+quarter\b/.test(lowerText)) {
    const lastQuarter = currentQuarter === 1 ? 4 : currentQuarter - 1
    const lastQuarterYear = currentQuarter === 1 ? currentYear - 1 : currentYear
    const dates = getQuarterDates(lastQuarterYear, lastQuarter)
    return { from: dates.start, to: dates.end }
  }

  // "this quarter" - current calendar quarter
  if (/this\s+quarter\b/.test(lowerText)) {
    const dates = getQuarterDates(currentYear, currentQuarter)
    return { from: dates.start, to: dates.end }
  }

  // "last year" - previous calendar year
  if (/last\s+year\b/.test(lowerText)) {
    return { from: `${currentYear - 1}-01-01`, to: `${currentYear - 1}-12-31` }
  }

  // "this year" - current calendar year
  if (/this\s+year\b/.test(lowerText)) {
    return { from: `${currentYear}-01-01`, to: `${currentYear}-12-31` }
  }

  // Absolute date range: "2024-01-01 2024-01-31" or "2024-01-01 to 2024-01-31"
  const absoluteRangeMatch = text.match(/\b(\d{4}-\d{2}-\d{2})\s+(?:to\s+)?(\d{4}-\d{2}-\d{2})\b/)
  if (absoluteRangeMatch) {
    return { from: absoluteRangeMatch[1], to: absoluteRangeMatch[2] }
  }

  // Single absolute date: "2024-01-01" (used as both from and to)
  const singleAbsoluteMatch = text.match(/\b(\d{4}-\d{2}-\d{2})\b/)
  if (singleAbsoluteMatch) {
    return { from: singleAbsoluteMatch[1], to: singleAbsoluteMatch[1] }
  }

  // Relative date format: "d-10 d0" or "d-10 d-1" (range)
  const rangeMatch = lowerText.match(/\b(d-?\d+)\s+(d-?\d+)\b/)
  if (rangeMatch) {
    return { from: rangeMatch[1], to: rangeMatch[2] }
  }

  // Single relative date: "d0", "d-1", "d-10" (used as both from and to)
  const singleMatch = lowerText.match(/\b(d-?\d+)\b/)
  if (singleMatch) {
    return { from: singleMatch[1], to: singleMatch[1] }
  }

  return null
}

// Helper to check if a value needs quoting (is a string, not a number, not already quoted)
function needsQuotes(value: string): boolean {
  const trimmed = value.trim()
  // Already quoted
  if ((trimmed.startsWith("'") && trimmed.endsWith("'")) ||
      (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
    return false
  }
  // Is a number
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return false
  }
  // Is a parenthesized list (for IN operator)
  if (trimmed.startsWith('(') && trimmed.endsWith(')')) {
    return false
  }
  return true
}

// Quote a single value if needed
function quoteValue(value: string): string {
  const trimmed = value.trim()
  if (needsQuotes(trimmed)) {
    return `'${trimmed}'`
  }
  return trimmed
}

// Quote values in a comma-separated list (for IN operator)
function quoteListValues(listStr: string): string {
  // Remove outer parentheses if present
  let inner = listStr.trim()
  if (inner.startsWith('(') && inner.endsWith(')')) {
    inner = inner.slice(1, -1)
  }

  // Split by comma and quote each value
  const values = inner.split(',').map(v => quoteValue(v.trim()))
  const result = values.join(', ')

  // Always wrap in parentheses for IN operator
  return `(${result})`
}

// Normalize a single clause (field operator value)
function normalizeClause(clause: string): string {
  const trimmed = clause.trim()

  // Handle "is null" / "is not null"
  const nullMatch = trimmed.match(/^(\w+)\s+(is\s+not\s+null|is\s+null)$/i)
  if (nullMatch) {
    const field = nullMatch[1]
    const isNotNull = /is\s+not\s+null/i.test(nullMatch[2])
    return isNotNull ? `${field} != ''` : `${field} = ''`
  }

  // Match field operator value
  const match = trimmed.match(/^(\w+)\s*(!=|>=|<=|=|>|<|not\s+in|in)\s*(.+)$/i)
  if (!match) return trimmed // Can't parse, return as-is

  const [, field, operator, value] = match
  const op = operator.trim().toLowerCase()
  let normalizedValue: string

  if (op === 'in' || op === 'not in') {
    normalizedValue = quoteListValues(value.trim())
  } else {
    normalizedValue = quoteValue(value.trim())
  }

  return `${field} ${operator} ${normalizedValue}`
}

// Normalize conditional by adding quotes to string values
function normalizeConditional(text: string): string {
  if (!text.trim()) return text

  // Split by AND/OR while preserving the operators
  const parts = text.split(/(\s+(?:and|or)\s+)/i)

  // Process each part - odd indices are the AND/OR operators
  const normalized = parts.map((part, index) => {
    if (index % 2 === 1) {
      // This is an AND/OR operator, keep as-is
      return part
    }
    // This is a clause, normalize it
    return normalizeClause(part)
  })

  return normalized.join('')
}

function validateConditional(
  text: string,
  metricOptions: Option[] = [],
  dimensionOptions: Option[] = []
): { valid: boolean; error?: string; normalized?: string } {
  if (!text.trim()) return { valid: true }

  // Check balanced parentheses
  let depth = 0
  for (const char of text) {
    if (char === '(') depth++
    if (char === ')') depth--
    if (depth < 0) return { valid: false, error: 'Unbalanced parentheses' }
  }
  if (depth !== 0) return { valid: false, error: 'Unbalanced parentheses' }

  // Remove parentheses for clause validation
  const normalized = text.replace(/[()]/g, ' ').trim()

  // Split by AND/OR to get individual clauses
  const clauses = normalized.split(/\s+(?:and|or)\s+/i).filter(c => c.trim())

  if (clauses.length === 0) return { valid: false, error: 'No conditions found' }

  // Helper to check if field is a dimension
  const isDimension = (field: string): boolean => {
    const lowerField = field.toLowerCase().trim()
    return dimensionOptions.some(d =>
      d.value.toLowerCase() === lowerField || d.label.toLowerCase() === lowerField
    )
  }

  // Helper to check if field is a metric
  const isMetric = (field: string): boolean => {
    const lowerField = field.toLowerCase().trim()
    return metricOptions.some(m =>
      m.value.toLowerCase() === lowerField || m.label.toLowerCase() === lowerField
    )
  }

  // Numeric operators that only make sense for metrics
  const numericOperators = ['>', '<', '>=', '<=']

  // String operators that only make sense for dimensions
  const stringOperators = ['like', 'not like']

  // Pattern for "is null" / "is not null" (no value needed)
  const nullPattern = /^(\w+)\s+(is\s+not\s+null|is\s+null)$/i
  // Valid operators regex (with value) - includes LIKE/NOT LIKE for string matching
  const operatorPattern = /^(.+?)\s*(!=|>=|<=|=|>|<|not\s+like|not\s+in|like|in)\s*(.+)$/i

  for (const clause of clauses) {
    const trimmed = clause.trim()
    if (!trimmed) continue

    // Check for null patterns first
    const nullMatch = trimmed.match(nullPattern)
    if (nullMatch) {
      const field = nullMatch[1]
      // Null checks are valid for dimensions only
      if (isMetric(field)) {
        return { valid: false, error: `"${field}" is a metric, use numeric comparison instead` }
      }
      continue
    }

    const match = trimmed.match(operatorPattern)
    if (!match) {
      return { valid: false, error: `Invalid condition: "${trimmed}"` }
    }

    const [, field, operator, value] = match
    const op = operator.trim()

    // Validate field exists
    if (!field.trim()) {
      return { valid: false, error: 'Missing field name' }
    }

    // Validate value exists
    if (!value.trim()) {
      return { valid: false, error: 'Missing value' }
    }

    // Check if numeric operator is used on a dimension
    if (numericOperators.includes(op) && isDimension(field)) {
      return { valid: false, error: `"${field}" is a dimension (text), cannot use "${op}" operator` }
    }

    // Check if string operator (LIKE) is used on a metric
    if (stringOperators.includes(op.toLowerCase()) && isMetric(field)) {
      return { valid: false, error: `"${field}" is a metric (number), cannot use "${op}" operator` }
    }

    // For IN/NOT IN, check for list format
    if (/^(not\s+)?in$/i.test(op)) {
      const valueStr = value.trim()
      if (!valueStr) {
        return { valid: false, error: `IN operator requires values` }
      }
      // IN/NOT IN only makes sense for dimensions
      if (isMetric(field)) {
        return { valid: false, error: `"${field}" is a metric, use numeric comparison instead of IN` }
      }
    }
  }

  return { valid: true }
}

function parseConditionals(text: string): string {
  // Find conditionals after "if", "where", or "conditional"
  const conditionalMatch = text.match(/(?:if|where|conditional)\s+(.+?)(?:$)/i)
  if (conditionalMatch) {
    let conditional = conditionalMatch[1].trim()

    // Remove any date phrases from the conditional
    conditional = conditional
      .replace(/\b(last|this)\s+\d+\s+(days?|weeks?|months?)\b/gi, '')
      .replace(/\b(yesterday|today)\b/gi, '')
      .replace(/\b(last|this)\s+(day|week|month|quarter|year)\b/gi, '')
      .trim()

    return conditional
  }

  return ''
}

function fuzzyMatch(text: string, options: Option[]): Option[] {
  if (!text.trim()) return []
  const lowerText = text.toLowerCase()
  const matches = options.filter(opt =>
    opt.label.toLowerCase().includes(lowerText) ||
    opt.value.toLowerCase().includes(lowerText)
  )
  // Sort by match quality: exact > prefix > contains
  return matches.sort((a, b) => {
    const aLabel = a.label.toLowerCase()
    const aValue = a.value.toLowerCase()
    const bLabel = b.label.toLowerCase()
    const bValue = b.value.toLowerCase()

    // Score: 3 = exact, 2 = prefix, 1 = contains
    const getScore = (label: string, value: string): number => {
      if (label === lowerText || value === lowerText) return 3
      if (label.startsWith(lowerText) || value.startsWith(lowerText)) return 2
      return 1
    }

    return getScore(bLabel, bValue) - getScore(aLabel, aValue)
  })
}

export function SmartReportInput({
  metricOptions,
  dimensionOptions,
  onQueryParsed,
  placeholder = "e.g., installs days pivot country last 10 days if spend > 1000",
  initialMetrics = [],
  initialDimensions = [],
  initialPivot = [],
  initialDateRange = null,
  initialConditionals = '',
  onFetchDimensionValues
}: SmartReportInputProps) {
  const [inputValue, setInputValue] = useState('')
  const [showHints, setShowHints] = useState(false)

  // Confirmed items (only shown as badges after Enter)
  const [confirmedMetrics, setConfirmedMetrics] = useState<string[]>(initialMetrics)
  const [confirmedDimensions, setConfirmedDimensions] = useState<string[]>(initialDimensions)
  const [confirmedPivot, setConfirmedPivot] = useState<string[]>(initialPivot)
  const [confirmedDateRange, setConfirmedDateRange] = useState<{ from?: string | Date; to?: string | Date } | null>(initialDateRange)
  const [confirmedConditionals, setConfirmedConditionals] = useState(initialConditionals)
  const [conditionalError, setConditionalError] = useState<string | null>(null)

  // Drag and drop state
  const [draggedBadge, setDraggedBadge] = useState<{ type: 'metric' | 'dimension' | 'pivot'; value: string; index: number } | null>(null)
  const [dropTarget, setDropTarget] = useState<{ type: 'metric' | 'dimension' | 'pivot'; index: number } | null>(null)

  // Dimension values autocomplete state
  const [dimensionValuesCache, setDimensionValuesCache] = useState<Record<string, string[]>>({})
  const [loadingDimensionValues, setLoadingDimensionValues] = useState(false)
  const [currentConditionalField, setCurrentConditionalField] = useState<string | null>(null)
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const inputRef = useRef<HTMLInputElement>(null)
  const hintsRef = useRef<HTMLDivElement>(null)
  const isSyncingFromProps = useRef(false)

  // Sync internal state with props when they change (e.g., switching from Advanced mode)
  // Use JSON.stringify to compare values, not references
  const initialMetricsKey = JSON.stringify(initialMetrics)
  const initialDimensionsKey = JSON.stringify(initialDimensions)
  const initialPivotKey = JSON.stringify(initialPivot)
  const initialDateRangeKey = JSON.stringify(initialDateRange)

  useEffect(() => {
    isSyncingFromProps.current = true
    setConfirmedMetrics(initialMetrics)
    // Reset flag after state update
    setTimeout(() => { isSyncingFromProps.current = false }, 0)
  }, [initialMetricsKey])

  useEffect(() => {
    isSyncingFromProps.current = true
    setConfirmedDimensions(initialDimensions)
    setTimeout(() => { isSyncingFromProps.current = false }, 0)
  }, [initialDimensionsKey])

  useEffect(() => {
    isSyncingFromProps.current = true
    setConfirmedPivot(initialPivot)
    setTimeout(() => { isSyncingFromProps.current = false }, 0)
  }, [initialPivotKey])

  useEffect(() => {
    isSyncingFromProps.current = true
    setConfirmedDateRange(initialDateRange)
    setTimeout(() => { isSyncingFromProps.current = false }, 0)
  }, [initialDateRangeKey])

  useEffect(() => {
    isSyncingFromProps.current = true
    setConfirmedConditionals(initialConditionals)
    setTimeout(() => { isSyncingFromProps.current = false }, 0)
  }, [initialConditionals])

  // Get label for a value from options
  const getLabel = (value: string, options: Option[]): string => {
    const option = options.find(o => o.value === value)
    return option?.label || value
  }

  // Remove confirmed items
  const removeMetric = (value: string) => setConfirmedMetrics(prev => prev.filter(m => m !== value))
  const removeDimension = (value: string) => setConfirmedDimensions(prev => prev.filter(d => d !== value))
  const removePivot = (value: string) => setConfirmedPivot(prev => prev.filter(p => p !== value))
  const clearDateRange = () => setConfirmedDateRange(null)
  const clearConditionals = () => setConfirmedConditionals('')
  const clearAll = () => {
    setConfirmedMetrics([])
    setConfirmedDimensions([])
    setConfirmedPivot([])
    setConfirmedDateRange(null)
    setConfirmedConditionals('')
    setConditionalError(null)
    setInputValue('')
  }

  // Drag and drop handlers - simple badge-to-badge dragging
  const handleDragStart = (e: React.DragEvent, type: 'metric' | 'dimension' | 'pivot', value: string, index: number) => {
    setDraggedBadge({ type, value, index })
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', value)
  }

  const handleDragEnter = (e: React.DragEvent, type: 'metric' | 'dimension' | 'pivot', index: number) => {
    e.preventDefault()
    if (draggedBadge && draggedBadge.type === type && draggedBadge.index !== index) {
      setDropTarget({ type, index })
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    // Only clear drop target if we're actually leaving the element, not just entering a child
    const relatedTarget = e.relatedTarget as Node | null
    const currentTarget = e.currentTarget as Node
    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      setDropTarget(null)
    }
  }

  const handleDrop = (e: React.DragEvent, type: 'metric' | 'dimension' | 'pivot', targetIndex: number) => {
    e.preventDefault()
    e.stopPropagation()

    if (!draggedBadge || draggedBadge.type !== type) {
      setDraggedBadge(null)
      setDropTarget(null)
      return
    }

    const sourceIndex = draggedBadge.index
    if (sourceIndex === targetIndex) {
      setDraggedBadge(null)
      setDropTarget(null)
      return
    }

    // Reorder array
    const reorder = (items: string[]) => {
      const newItems = [...items]
      const [removed] = newItems.splice(sourceIndex, 1)
      newItems.splice(targetIndex, 0, removed)
      return newItems
    }

    if (type === 'metric') {
      setConfirmedMetrics(reorder)
    } else if (type === 'dimension') {
      setConfirmedDimensions(reorder)
    } else if (type === 'pivot') {
      setConfirmedPivot(reorder)
    }

    setDraggedBadge(null)
    setDropTarget(null)
  }

  const handleDragEnd = () => {
    setDraggedBadge(null)
    setDropTarget(null)
  }

  // Parse the query and extract components
  const parseQuery = (text: string): ParsedQuery => {
    const result: ParsedQuery = {
      metrics: [],
      dimensions: [],
      pivot: [],
      dateRange: null,
      conditionals: ''
    }

    // Extract conditionals first (everything after if/where/conditional)
    result.conditionals = parseConditionals(text)

    // Extract date range
    result.dateRange = parseDatePhrase(text)

    // Remove date phrases and conditionals for metric/dimension parsing
    let cleanText = text.toLowerCase()
      .replace(/\b(last|this)\s+\d+\s+(days?|weeks?|months?)\b/gi, '')
      .replace(/\b(yesterday|today)\b/gi, '')
      .replace(/\b(last|this)\s+(day|week|month|quarter|year)\b/gi, '')
      .replace(/\bd-?\d+\b/gi, '') // Remove relative dates like d0, d-1, d-10
      .replace(/\b\d{4}-\d{2}-\d{2}\b/g, '') // Remove absolute dates like 2024-01-01
      .replace(/\bto\b/gi, '') // Remove "to" keyword used between dates
      .replace(/(?:if|where|conditional)\s+.+$/i, '')
      .trim()

    // Extract pivot text using regex (handles start of string and various spacing)
    let pivotText = ''
    const pivotMatch = cleanText.match(/(?:^|\s)pivot\s+(.+?)$/i)
    if (pivotMatch) {
      pivotText = pivotMatch[1].trim()
      cleanText = cleanText.replace(/(?:^|\s)pivot\s+.+$/i, '').trim()
    }

    // Check if "by" keyword is present for explicit separation
    const byIndex = cleanText.indexOf(' by ')
    const hasByKeyword = byIndex > -1

    // Helper to find best match for a word (exact > prefix > contains)
    const findMatch = (word: string, options: Option[]): Option | null => {
      if (word.length < 2) return null

      // First, look for exact match
      const exactMatch = options.find(opt =>
        opt.label.toLowerCase() === word ||
        opt.value.toLowerCase() === word
      )
      if (exactMatch) return exactMatch

      // Second, look for prefix match
      const prefixMatch = options.find(opt =>
        opt.label.toLowerCase().startsWith(word) ||
        opt.value.toLowerCase().startsWith(word)
      )
      if (prefixMatch) return prefixMatch

      // Fall back to contains match
      const containsMatch = options.find(opt =>
        opt.label.toLowerCase().includes(word) ||
        opt.value.toLowerCase().includes(word)
      )
      return containsMatch || null
    }

    if (hasByKeyword) {
      // Explicit mode: words before "by" are metrics, after are dimensions
      const metricsText = cleanText.substring(0, byIndex)
      const dimensionsText = cleanText.substring(byIndex + 4)

      const metricsWords = metricsText.split(/\s+/).filter((w: string) => w.length > 1)
      const dimensionsWords = dimensionsText.split(/\s+/).filter((w: string) => w.length > 1)

      for (const word of metricsWords) {
        const match = findMatch(word, metricOptions)
        if (match && !result.metrics.includes(match.value)) {
          result.metrics.push(match.value)
        }
      }

      for (const word of dimensionsWords) {
        const match = findMatch(word, dimensionOptions)
        if (match && !result.dimensions.includes(match.value)) {
          result.dimensions.push(match.value)
        }
      }
    } else {
      // Smart mode: try metrics first, then dimensions
      const words = cleanText.split(/\s+/).filter((w: string) => w.length > 1)

      // Date-related tokens to skip (these are handled by parseDatePhrase)
      const dateUnits = ['day', 'days', 'week', 'weeks', 'month', 'months', 'quarter', 'year']
      let inDatePhrase = false

      for (const word of words) {
        // Skip date tokens - "last" and "this" start a date phrase
        if (word === 'last' || word === 'this') {
          inDatePhrase = true
          continue
        }
        // Skip numbers and date units within a date phrase
        if (inDatePhrase) {
          if (/^\d+$/.test(word) || dateUnits.includes(word)) {
            // End date phrase after the unit word
            if (dateUnits.includes(word)) inDatePhrase = false
            continue
          }
          // Not a date unit, end the date phrase
          inDatePhrase = false
        }
        // Skip standalone date words
        if (word === 'yesterday' || word === 'today') {
          continue
        }
        // Skip relative date formats like d0, d-1, d-10
        if (/^d-?\d+$/.test(word)) {
          continue
        }

        // Try matching as metric first
        const metricMatch = findMatch(word, metricOptions)
        if (metricMatch && !result.metrics.includes(metricMatch.value)) {
          result.metrics.push(metricMatch.value)
          continue
        }

        // If not a metric, try as dimension
        const dimensionMatch = findMatch(word, dimensionOptions)
        if (dimensionMatch && !result.dimensions.includes(dimensionMatch.value)) {
          result.dimensions.push(dimensionMatch.value)
        }
      }
    }

    // Match pivot words
    const pivotWords = pivotText.split(/\s+/).filter((w: string) => w.length > 1)
    for (const word of pivotWords) {
      const match = findMatch(word, dimensionOptions)
      if (match && !result.pivot.includes(match.value)) {
        result.pivot.push(match.value)
      }
    }

    return result
  }

  // Emit confirmed items to parent (skip during prop sync to avoid infinite loop)
  useEffect(() => {
    if (isSyncingFromProps.current) return
    onQueryParsed({
      metrics: confirmedMetrics,
      dimensions: confirmedDimensions,
      pivot: confirmedPivot,
      dateRange: confirmedDateRange,
      conditionals: confirmedConditionals
    })
  }, [confirmedMetrics, confirmedDimensions, confirmedPivot, confirmedDateRange, confirmedConditionals])

  // Confirm input - parse and add to badges
  const confirmInput = () => {
    if (!inputValue.trim()) return

    const parsed = parseQuery(inputValue)

    // Validate conditionals before accepting
    if (parsed.conditionals) {
      const validation = validateConditional(parsed.conditionals, metricOptions, dimensionOptions)
      if (!validation.valid) {
        setConditionalError(validation.error || 'Invalid conditional')
        return // Don't confirm anything if conditional is invalid
      }
    }

    // Clear any previous error
    setConditionalError(null)

    // Add to confirmed lists (avoid duplicates)
    if (parsed.metrics.length > 0) {
      setConfirmedMetrics(prev => [...new Set([...prev, ...parsed.metrics])])
    }
    if (parsed.dimensions.length > 0) {
      setConfirmedDimensions(prev => [...new Set([...prev, ...parsed.dimensions])])
    }
    if (parsed.pivot.length > 0) {
      setConfirmedPivot(prev => [...new Set([...prev, ...parsed.pivot])])
    }
    if (parsed.dateRange) {
      setConfirmedDateRange(parsed.dateRange)
    }
    if (parsed.conditionals) {
      // Normalize the conditional to add quotes to string values
      const normalizedConditional = normalizeConditional(parsed.conditionals)
      setConfirmedConditionals(normalizedConditional)
    }

    // Check if user typed "pivot" but no pivot dimension was parsed
    // Keep "pivot " in the input so they can continue selecting pivot dimensions
    const lowerInput = inputValue.toLowerCase().trim()
    const hasPivotKeyword = /\bpivot\b/.test(lowerInput)
    const pivotWasAdded = parsed.pivot.length > 0

    // Check if user typed "if" but no conditional was parsed
    // Keep "if " in the input so they can continue typing conditionals
    const hasIfKeyword = /\bif\b/.test(lowerInput)
    const conditionalWasAdded = parsed.conditionals.length > 0

    if (hasPivotKeyword && !pivotWasAdded) {
      // Keep "pivot " in input for user to continue adding pivot dimensions
      setInputValue('pivot ')
    } else if (hasIfKeyword && !conditionalWasAdded) {
      // Keep "if " in input for user to continue typing conditionals
      setInputValue('if ')
    } else {
      // Clear input after confirmation
      setInputValue('')
    }
    setShowHints(false)
  }

  // Handle keyboard
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Arrow navigation in dropdown
    if (showHints && hasHints && allHints.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedHintIndex(prev => Math.min(prev + 1, allHints.length - 1))
        return
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedHintIndex(prev => Math.max(prev - 1, -1))
        return
      } else if (e.key === 'Enter' && selectedHintIndex >= 0 && selectedHintIndex < allHints.length) {
        e.preventDefault()
        selectHintItem(allHints[selectedHintIndex])
        return
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setShowHints(false)
        setSelectedHintIndex(-1)
        return
      }
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      confirmInput()
    } else if (e.key === 'Backspace' && inputValue === '') {
      // Remove last badge if input is empty
      if (confirmedConditionals) {
        clearConditionals()
      } else if (confirmedDateRange) {
        clearDateRange()
      } else if (confirmedPivot.length > 0) {
        setConfirmedPivot(prev => prev.slice(0, -1))
      } else if (confirmedDimensions.length > 0) {
        setConfirmedDimensions(prev => prev.slice(0, -1))
      } else if (confirmedMetrics.length > 0) {
        setConfirmedMetrics(prev => prev.slice(0, -1))
      }
    }
  }

  // Detect if we're in conditional value context (e.g., "if app = " or "field != ")
  // Returns the field name if in context, null otherwise
  const detectConditionalValueContext = (text: string): { field: string; partialValue: string } | null => {
    // Pattern: field operator [partial_value]
    // Operators: =, !=, >, <, >=, <=, in, not in
    const match = text.match(/(?:if|where|conditional)\s+.*?(\w+)\s*(!=|>=|<=|=|>|<|not\s+in|in)\s*(\S*)$/i)
    if (match) {
      const field = match[1]
      const partialValue = match[3] || ''
      // Check if field is a dimension
      const isDimension = dimensionOptions.some(d =>
        d.value.toLowerCase() === field.toLowerCase() ||
        d.label.toLowerCase() === field.toLowerCase()
      )
      if (isDimension) {
        return { field, partialValue }
      }
    }
    return null
  }

  // Fetch dimension values when in conditional context
  useEffect(() => {
    const context = detectConditionalValueContext(inputValue)

    if (!context || !onFetchDimensionValues) {
      setCurrentConditionalField(null)
      return
    }

    const { field } = context

    // Already have cached values for this field
    if (dimensionValuesCache[field]) {
      setCurrentConditionalField(field)
      return
    }

    // Debounce the fetch
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current)
    }

    setCurrentConditionalField(field)
    setLoadingDimensionValues(true)

    fetchTimeoutRef.current = setTimeout(async () => {
      try {
        console.log('🔍 Fetching dimension values for:', field)
        const values = await onFetchDimensionValues(field)
        console.log('🔍 Received values:', values)
        setDimensionValuesCache(prev => {
          const newCache = { ...prev, [field]: values }
          console.log('🔍 Updated cache:', newCache)
          return newCache
        })
      } catch (error) {
        console.error('Failed to fetch dimension values:', error)
      } finally {
        setLoadingDimensionValues(false)
      }
    }, 300) // 300ms debounce

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current)
      }
    }
  }, [inputValue, onFetchDimensionValues, dimensionValuesCache, dimensionOptions])

  // Check if we're in pivot context for hints display
  const isInPivotContext = useMemo(() => {
    const lowerText = inputValue.toLowerCase()
    return /\bpivot\s+\S*$/.test(lowerText)
  }, [inputValue])

  // Get hints for the current word being typed (informational only)
  const hints = useMemo(() => {
    if (!inputValue.trim()) return { metrics: [], dimensions: [], values: [], loadingValues: false, inPivotContext: false }

    // Check if we're in conditional value context first
    const conditionalContext = detectConditionalValueContext(inputValue)
    if (conditionalContext && currentConditionalField) {
      const { field, partialValue } = conditionalContext
      const cachedValues = dimensionValuesCache[field] || []
      console.log('🎯 Hints conditional context:', { field, partialValue, cachedValues, loadingDimensionValues, cache: dimensionValuesCache })

      // Filter values by partial input (convert to string for numeric fields like integration_id)
      const filteredValues = partialValue
        ? cachedValues.filter(v =>
            String(v).toLowerCase().includes(partialValue.toLowerCase())
          ).slice(0, 10)
        : cachedValues.slice(0, 10)

      return {
        metrics: [],
        dimensions: [],
        periods: [],
        values: filteredValues,
        valueField: field,
        loadingValues: loadingDimensionValues,
        inPivotContext: false
      }
    }

    // Get the words being typed
    const words = inputValue.toLowerCase().split(/\s+/)
    const lastWord = words[words.length - 1] || ''

    if (lastWord.length < 2) return { metrics: [], dimensions: [], values: [], loadingValues: false, inPivotContext: isInPivotContext }

    // Don't show hints for keywords (except "last" and "this" which show period hints)
    const keywords = ['by', 'pivot', 'if', 'where', 'conditional', 'and', 'or']
    if (keywords.includes(lastWord)) return { metrics: [], dimensions: [], periods: [], values: [], loadingValues: false, inPivotContext: isInPivotContext }

    // Period options with format hints
    const periodOptions = [
      { label: 'd0, d-1,...d-N', value: 'd' },
      { label: 'day', value: 'day' },
      { label: 'week', value: 'week' },
      { label: 'month', value: 'month' },
      { label: 'quarter', value: 'quarter' },
      { label: 'year', value: 'year' },
      { label: 'today', value: 'today' },
      { label: 'yesterday', value: 'yesterday' },
      { label: 'X days', value: 'days' },
      { label: 'X weeks', value: 'weeks' },
      { label: 'X months', value: 'months' },
    ]

    // Show all period options when "last" or "this" is typed
    if (lastWord === 'last' || lastWord === 'this') {
      return { metrics: [], dimensions: [], periods: periodOptions, values: [], loadingValues: false }
    }

    // Skip showing hints for relative date format (d0, d-1, d-10, etc.)
    if (/^d-?\d*$/.test(lastWord)) {
      return { metrics: [], dimensions: [], periods: [], values: [], loadingValues: false }
    }

    // Check if we're in a date phrase (previous words contain "last" or "this")
    const prevWords = words.slice(0, -1)
    const inDatePhrase = prevWords.some((w, i) => {
      if (w === 'last' || w === 'this') {
        // Check if all words after are numbers or we're at the date unit
        const afterWords = prevWords.slice(i + 1)
        return afterWords.every(aw => /^\d+$/.test(aw)) || afterWords.length === 0
      }
      return false
    })

    // Show filtered period options when in a date phrase
    if (inDatePhrase) {
      const matchingPeriods = periodOptions
        .filter(p => p.value.startsWith(lastWord) || lastWord.length < 2)
      return { metrics: [], dimensions: [], periods: matchingPeriods, values: [], loadingValues: false }
    }

    const matchingMetrics = fuzzyMatch(lastWord, metricOptions).slice(0, 5)
    const matchingDimensions = fuzzyMatch(lastWord, dimensionOptions).slice(0, 5)

    return {
      // Don't show metrics when in pivot context - only dimensions
      metrics: isInPivotContext ? [] : matchingMetrics,
      dimensions: matchingDimensions,
      periods: [],
      values: [],
      loadingValues: false,
      inPivotContext: isInPivotContext
    }
  }, [inputValue, metricOptions, dimensionOptions, currentConditionalField, dimensionValuesCache, loadingDimensionValues, isInPivotContext])

  const hasHints = hints.metrics.length > 0 || hints.dimensions.length > 0 || (hints.periods?.length || 0) > 0 || (hints.values?.length || 0) > 0 || hints.loadingValues

  // Keyboard navigation state for dropdown
  const [selectedHintIndex, setSelectedHintIndex] = useState(-1)

  // Build flat list of all hints for keyboard navigation
  const allHints = useMemo(() => {
    const items: Array<{ type: 'metric' | 'dimension' | 'period' | 'value'; label: string; value: string }> = []
    hints.metrics.forEach(m => items.push({ type: 'metric', label: m.label, value: m.value }))
    hints.dimensions.forEach(d => items.push({ type: 'dimension', label: d.label, value: d.value }))
    hints.periods?.forEach(p => items.push({ type: 'period', label: p.label, value: p.value }))
    hints.values?.forEach(v => items.push({ type: 'value', label: String(v), value: String(v) }))
    return items
  }, [hints])

  // Reset selected index when hints change
  useEffect(() => {
    setSelectedHintIndex(-1)
  }, [inputValue])

  // Check if "pivot" keyword appears before the current word being typed
  const isPivotContext = useCallback((text: string): boolean => {
    const lowerText = text.toLowerCase()
    // Check if "pivot" appears and the current word is after it
    const pivotMatch = lowerText.match(/\bpivot\s+\S*$/)
    return !!pivotMatch
  }, [])

  // Handle selecting a hint item
  const selectHintItem = useCallback((item: { type: 'metric' | 'dimension' | 'period' | 'value'; label: string; value: string }) => {
    if (item.type === 'metric') {
      if (!confirmedMetrics.includes(item.value)) {
        setConfirmedMetrics(prev => [...prev, item.value])
      }
      // Remove the partial word from input
      const words = inputValue.split(/\s+/)
      words.pop()
      setInputValue(words.join(' ') + (words.length > 0 ? ' ' : ''))
    } else if (item.type === 'dimension') {
      // Check if we're in pivot context (user typed "pivot" before this word)
      const inPivotContext = isPivotContext(inputValue)

      if (inPivotContext) {
        // Add to pivot list instead of dimensions
        if (!confirmedPivot.includes(item.value)) {
          setConfirmedPivot(prev => [...prev, item.value])
        }
      } else {
        if (!confirmedDimensions.includes(item.value)) {
          setConfirmedDimensions(prev => [...prev, item.value])
        }
      }
      // Remove the partial word (and "pivot" keyword if in pivot context) from input
      const words = inputValue.split(/\s+/)
      words.pop() // Remove partial word
      if (inPivotContext && words.length > 0 && words[words.length - 1].toLowerCase() === 'pivot') {
        words.pop() // Also remove "pivot" keyword
      }
      setInputValue(words.join(' ') + (words.length > 0 ? ' ' : ''))
    } else if (item.type === 'period') {
      // For periods, append to input (user is building a date phrase like "last 10 days")
      const words = inputValue.split(/\s+/)
      words.pop()
      setInputValue(words.join(' ') + (words.length > 0 ? ' ' : '') + item.value + ' ')
    } else if (item.type === 'value') {
      // For values in conditional context, append the value to input
      const conditionalContext = detectConditionalValueContext(inputValue)
      if (conditionalContext) {
        // Replace partial value with selected value
        const beforeValue = inputValue.substring(0, inputValue.lastIndexOf(conditionalContext.partialValue) || inputValue.length)
        setInputValue(beforeValue + item.value + ' ')
      }
    }
    setShowHints(false)
    setSelectedHintIndex(-1)
    inputRef.current?.focus()
  }, [inputValue, confirmedMetrics, confirmedDimensions, confirmedPivot, isPivotContext])

  // Click outside to close hints
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        hintsRef.current &&
        !hintsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowHints(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Check if we have any confirmed items
  const hasConfirmedItems = confirmedMetrics.length > 0 ||
    confirmedDimensions.length > 0 ||
    confirmedPivot.length > 0 ||
    confirmedDateRange !== null ||
    confirmedConditionals !== ''

  // Format date range for display
  const formatDateRange = (dateRange: { from?: string | Date; to?: string | Date }): string => {
    const formatPart = (part: string | Date | undefined): string => {
      if (!part) return ''
      if (typeof part === 'string') return part
      return part.toLocaleDateString()
    }
    return `${formatPart(dateRange.from)} → ${formatPart(dateRange.to)}`
  }

  return (
    <div className="relative w-full">
      {/* Combined input with inline badges */}
      <div
        className={`flex flex-wrap items-center gap-1.5 min-h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 cursor-text overflow-hidden ${
          conditionalError ? 'border-destructive' : 'border-input'
        }`}
        onClick={() => inputRef.current?.focus()}
      >
        {hasConfirmedItems && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); clearAll() }}
            className="h-4 w-4 text-muted-foreground hover:text-foreground flex-shrink-0 flex items-center justify-center"
            title="Clear all"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}

        {/* Confirmed badges - draggable for reordering (drag onto another badge to swap) */}
        {confirmedMetrics.map((metric, index) => {
          const isDragging = draggedBadge?.type === 'metric' && draggedBadge?.index === index
          const isHovered = dropTarget?.type === 'metric' && dropTarget?.index === index
          return (
            <div
              key={metric}
              draggable
              onDragStart={(e) => handleDragStart(e, 'metric', metric, index)}
              onDragEnter={(e) => handleDragEnter(e, 'metric', index)}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'metric', index)}
              onDragEnd={handleDragEnd}
              className={isHovered ? 'ring-2 ring-blue-500 ring-offset-2 rounded-md' : ''}
            >
              <Badge
                className={`bg-blue-100 text-blue-800 border-blue-200 flex-shrink-0 cursor-grab active:cursor-grabbing hover:bg-blue-200 flex items-center gap-1 ${
                  isDragging ? 'opacity-50' : ''
                } ${isHovered ? 'bg-blue-200 scale-105' : ''}`}
              >
                <GripVertical className="h-3 w-3 text-blue-400" />
                {getLabel(metric, metricOptions)}
                <span onClick={(e) => { e.stopPropagation(); removeMetric(metric) }} className="hover:text-blue-900 cursor-pointer">×</span>
              </Badge>
            </div>
          )
        })}

        {confirmedDimensions.map((dimension, index) => {
          const isDragging = draggedBadge?.type === 'dimension' && draggedBadge?.index === index
          const isHovered = dropTarget?.type === 'dimension' && dropTarget?.index === index
          return (
            <div
              key={dimension}
              draggable
              onDragStart={(e) => handleDragStart(e, 'dimension', dimension, index)}
              onDragEnter={(e) => handleDragEnter(e, 'dimension', index)}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'dimension', index)}
              onDragEnd={handleDragEnd}
              className={isHovered ? 'ring-2 ring-green-500 ring-offset-2 rounded-md' : ''}
            >
              <Badge
                className={`bg-green-100 text-green-800 border-green-200 flex-shrink-0 cursor-grab active:cursor-grabbing hover:bg-green-200 flex items-center gap-1 ${
                  isDragging ? 'opacity-50' : ''
                } ${isHovered ? 'bg-green-200 scale-105' : ''}`}
              >
                <GripVertical className="h-3 w-3 text-green-400" />
                {getLabel(dimension, dimensionOptions)}
                <span onClick={(e) => { e.stopPropagation(); removeDimension(dimension) }} className="hover:text-green-900 cursor-pointer">×</span>
              </Badge>
            </div>
          )
        })}

        {confirmedPivot.map((pivot, index) => {
          const isDragging = draggedBadge?.type === 'pivot' && draggedBadge?.index === index
          const isHovered = dropTarget?.type === 'pivot' && dropTarget?.index === index
          return (
            <div
              key={pivot}
              draggable
              onDragStart={(e) => handleDragStart(e, 'pivot', pivot, index)}
              onDragEnter={(e) => handleDragEnter(e, 'pivot', index)}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'pivot', index)}
              onDragEnd={handleDragEnd}
              className={isHovered ? 'ring-2 ring-purple-500 ring-offset-2 rounded-md' : ''}
            >
              <Badge
                className={`bg-purple-100 text-purple-800 border-purple-200 flex-shrink-0 cursor-grab active:cursor-grabbing hover:bg-purple-200 flex items-center gap-1 ${
                  isDragging ? 'opacity-50' : ''
                } ${isHovered ? 'bg-purple-200 scale-105' : ''}`}
              >
                <GripVertical className="h-3 w-3 text-purple-400" />
                ↻ {getLabel(pivot, dimensionOptions)}
                <span onClick={(e) => { e.stopPropagation(); removePivot(pivot) }} className="hover:text-purple-900 cursor-pointer">×</span>
              </Badge>
            </div>
          )
        })}
        {confirmedDateRange && (
          <Badge
            className="bg-amber-100 text-amber-800 border-amber-200 flex-shrink-0 cursor-pointer hover:bg-amber-200"
            onClick={(e) => { e.stopPropagation(); clearDateRange() }}
          >
            📅 {formatDateRange(confirmedDateRange)} ×
          </Badge>
        )}
        {confirmedConditionals && (
          <Badge
            className="bg-rose-100 text-rose-800 border-rose-200 cursor-pointer hover:bg-rose-200 max-w-[300px] truncate"
            onClick={(e) => { e.stopPropagation(); clearConditionals() }}
            title={`if: ${confirmedConditionals}`}
          >
            if: {confirmedConditionals} ×
          </Badge>
        )}

        {/* Text input */}
        <input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value)
            setShowHints(true)
            if (conditionalError) setConditionalError(null)
          }}
          onFocus={() => setShowHints(true)}
          onBlur={() => {
            setTimeout(() => setShowHints(false), 200)
            // Validate conditionals on blur (like Advanced mode)
            const parsed = parseQuery(inputValue)
            const conditionalToValidate = parsed.conditionals || confirmedConditionals
            if (conditionalToValidate) {
              const validation = validateConditional(conditionalToValidate, metricOptions, dimensionOptions)
              if (!validation.valid) {
                setConditionalError(validation.error || 'Invalid conditional')
              } else {
                setConditionalError(null)
              }
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={hasConfirmedItems ? "" : placeholder}
          className="flex-1 min-w-[150px] bg-transparent outline-none placeholder:text-muted-foreground"
        />

        {/* Help tooltip */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="flex-shrink-0 text-muted-foreground hover:text-foreground"
                onClick={(e) => e.stopPropagation()}
              >
                <HelpCircle className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-xs">
                Type metrics, then "by" for dimensions, "pivot" for pivot fields, date phrases like "last 10 days", and "if" for conditionals
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Conditional validation error */}
      {conditionalError && (
        <p className="text-xs text-destructive mt-1">{conditionalError}</p>
      )}

      {/* Hints dropdown - clickable items */}
      {showHints && hasHints && (
        <div
          ref={hintsRef}
          className="absolute z-50 mt-1 w-full max-w-md bg-popover border rounded-md shadow-lg overflow-hidden"
        >
          <div className="max-h-60 overflow-y-auto py-1">
            {/* Metrics section */}
            {hints.metrics.length > 0 && (
              <>
                <div className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50/50 border-b">
                  Metrics
                </div>
                {hints.metrics.map((m, idx) => {
                  const globalIdx = idx
                  const isSelected = selectedHintIndex === globalIdx
                  return (
                    <div
                      key={m.value}
                      className={`px-3 py-1.5 text-sm cursor-pointer ${
                        isSelected ? 'bg-blue-100 text-blue-900' : 'text-blue-700 hover:bg-muted'
                      }`}
                      onClick={() => selectHintItem({ type: 'metric', label: m.label, value: m.value })}
                      onMouseEnter={() => setSelectedHintIndex(globalIdx)}
                    >
                      {m.label}
                    </div>
                  )
                })}
              </>
            )}

            {/* Dimensions section - purple when in pivot context, green otherwise */}
            {hints.dimensions.length > 0 && (
              <>
                <div className={`px-2 py-1 text-xs font-medium border-b border-t ${
                  hints.inPivotContext
                    ? 'text-purple-600 bg-purple-50/50'
                    : 'text-green-600 bg-green-50/50'
                }`}>
                  {hints.inPivotContext ? 'Pivot Dimensions' : 'Dimensions'}
                </div>
                {hints.dimensions.map((d, idx) => {
                  const globalIdx = hints.metrics.length + idx
                  const isSelected = selectedHintIndex === globalIdx
                  return (
                    <div
                      key={d.value}
                      className={`px-3 py-1.5 text-sm cursor-pointer ${
                        hints.inPivotContext
                          ? (isSelected ? 'bg-purple-100 text-purple-900' : 'text-purple-700 hover:bg-muted')
                          : (isSelected ? 'bg-green-100 text-green-900' : 'text-green-700 hover:bg-muted')
                      }`}
                      onClick={() => selectHintItem({ type: 'dimension', label: d.label, value: d.value })}
                      onMouseEnter={() => setSelectedHintIndex(globalIdx)}
                    >
                      {d.label}
                    </div>
                  )
                })}
              </>
            )}

            {/* Periods section */}
            {hints.periods && hints.periods.length > 0 && (
              <>
                <div className="px-2 py-1 text-xs font-medium text-amber-600 bg-amber-50/50 border-b border-t">
                  Periods
                </div>
                {hints.periods.map((p, idx) => {
                  const globalIdx = hints.metrics.length + hints.dimensions.length + idx
                  const isSelected = selectedHintIndex === globalIdx
                  return (
                    <div
                      key={p.value}
                      className={`px-3 py-1.5 text-sm cursor-pointer ${
                        isSelected ? 'bg-amber-100 text-amber-900' : 'text-amber-700 hover:bg-muted'
                      }`}
                      onClick={() => selectHintItem({ type: 'period', label: p.label, value: p.value })}
                      onMouseEnter={() => setSelectedHintIndex(globalIdx)}
                    >
                      {p.label}
                    </div>
                  )
                })}
              </>
            )}

            {/* Loading values indicator */}
            {hints.loadingValues && (
              <div className="px-3 py-2 text-sm text-muted-foreground italic">
                Loading values...
              </div>
            )}

            {/* Values section (for conditional autocomplete) */}
            {!hints.loadingValues && hints.values && hints.values.length > 0 && (
              <>
                <div className="px-2 py-1 text-xs font-medium text-rose-600 bg-rose-50/50 border-b border-t">
                  Values for {hints.valueField}
                </div>
                {hints.values.map((v, idx) => {
                  const globalIdx = hints.metrics.length + hints.dimensions.length + (hints.periods?.length || 0) + idx
                  const isSelected = selectedHintIndex === globalIdx
                  return (
                    <div
                      key={String(v)}
                      className={`px-3 py-1.5 text-sm cursor-pointer ${
                        isSelected ? 'bg-rose-100 text-rose-900' : 'text-rose-700 hover:bg-muted'
                      }`}
                      onClick={() => selectHintItem({ type: 'value', label: String(v), value: String(v) })}
                      onMouseEnter={() => setSelectedHintIndex(globalIdx)}
                    >
                      {String(v)}
                    </div>
                  )
                })}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// Export validation utilities for use in Advanced mode
export { validateConditional, normalizeConditional }
export type { Option as ConditionalFieldOption }
